import { gql } from 'graphql-request';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gqlClient } from '@/api';

/**
 * The reports people send from the website, as the admin reads and answers
 * them.
 *
 * The screen this feeds is a mailbox that cannot lose anything: every report
 * is a row, every reply is written down before it is sent, and a report the
 * spam checks mistrusted is on a shelf rather than in a bin. What the studio
 * does here is decide which of those is which.
 */

export type TicketStatus = 'NEW' | 'OPEN' | 'WAITING' | 'CLOSED' | 'SPAM';
export type TicketKind = 'BUG' | 'QUESTION' | 'IDEA' | 'OTHER';

/** What each status is called on screen, and in what order they are offered. */
export const TICKET_STATUSES: { value: TicketStatus; label: string }[] = [
  { value: 'NEW', label: 'New' },
  { value: 'OPEN', label: 'Open' },
  { value: 'WAITING', label: 'Waiting on them' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'SPAM', label: 'Spam' },
];

export const TICKET_KINDS: Record<TicketKind, string> = {
  BUG: 'Bug',
  QUESTION: 'Question',
  IDEA: 'Idea',
  OTHER: 'Message',
};

export const statusLabel = (status: string): string =>
  TICKET_STATUSES.find((one) => one.value === status)?.label ?? status;

export interface TicketRow {
  id: string;
  reference: string;
  kind: TicketKind;
  status: TicketStatus;
  subject: string;
  email: string;
  name: string | null;
  appName: string | null;
  appSlug: string | null;
  locale: string;
  createdAt: string;
  /** When either side last wrote. What the list is sorted by. */
  lastMessageAt: string;
  /** Whether the studio has ever answered — the list marks the ones it has not. */
  replied: boolean;
}

export interface TicketList {
  items: TicketRow[];
  total: number;
  /** Reports still waiting on the studio, for the badge on the rail. */
  unanswered: number;
}

export interface TicketMessage {
  id: string;
  kind: 'VISITOR' | 'STUDIO' | 'NOTE';
  body: string;
  authorName: string | null;
  /** For a reply: whether the mail server took it. */
  emailSent: boolean;
  createdAt: string;
}

export interface TicketDetail extends TicketRow {
  appVersion: string | null;
  systemInfo: string | null;
  attachmentUrl: string | null;
  spamScore: number | null;
  closedAt: string | null;
  messages: TicketMessage[];
}

/** Rows on one page. The same size the other admin tables use. */
export const TICKETS_PER_PAGE = 50;

const ROW_FIELDS = `
  id
  reference
  kind
  status
  subject
  email
  name
  appName
  appSlug
  locale
  createdAt
  lastMessageAt
  replied
`;

const DETAIL_FIELDS = `
  ${ROW_FIELDS}
  appVersion
  systemInfo
  attachmentUrl
  spamScore
  closedAt
  messages {
    id
    kind
    body
    authorName
    emailSent
    createdAt
  }
`;

const TICKETS = gql`
  query Tickets($status: TicketStatus, $appId: ID, $limit: Int, $offset: Int) {
    tickets(status: $status, appId: $appId, limit: $limit, offset: $offset) {
      items { ${ROW_FIELDS} }
      total
      unanswered
    }
  }
`;

const WAITING = gql`
  query TicketsWaiting($limit: Int) {
    tickets(limit: $limit) {
      unanswered
    }
  }
`;

const TICKET = gql`
  query Ticket($id: ID!) {
    ticket(id: $id) { ${DETAIL_FIELDS} }
  }
`;

const SET_STATUS = gql`
  mutation SetTicketStatus($id: ID!, $status: TicketStatus!) {
    setTicketStatus(id: $id, status: $status) { ${DETAIL_FIELDS} }
  }
`;

const REPLY = gql`
  mutation ReplyToTicket($input: ReplyToTicketInput!) {
    replyToTicket(input: $input) { ${DETAIL_FIELDS} }
  }
`;

const ADD_NOTE = gql`
  mutation AddTicketNote($input: AddTicketNoteInput!) {
    addTicketNote(input: $input) { ${DETAIL_FIELDS} }
  }
`;

export const useTickets = (filters: { status?: TicketStatus | null; page?: number } = {}) =>
  useQuery({
    // The filter and the page are in the key, so switching either refetches
    // rather than showing one filter's rows under another's heading.
    queryKey: ['tickets', filters.status ?? null, filters.page ?? 1],
    queryFn: async () => {
      const data = await gqlClient.request<{ tickets: TicketList }>(TICKETS, {
        status: filters.status ?? null,
        limit: TICKETS_PER_PAGE,
        offset: ((filters.page ?? 1) - 1) * TICKETS_PER_PAGE,
      });
      return data.tickets;
    },
    // Keep the rows while the next page loads, rather than emptying the table.
    placeholderData: keepPreviousData,
  });

/**
 * How many reports are waiting on the studio, for the badge on the rail.
 *
 * The same query the list makes, asked for one row — so it shares nothing
 * with the list's cache (a different key) and costs one small request per
 * screen rather than a second list. `unanswered` is counted over every page
 * by the API, so the page size here changes nothing about the number.
 */
export const useWaitingTickets = (): number => {
  const { data } = useQuery({
    queryKey: ['ticketsWaiting'],
    queryFn: async () => {
      const result = await gqlClient.request<{ tickets: { unanswered: number } }>(WAITING, {
        limit: 1,
      });
      return result.tickets.unanswered;
    },
    /*
     * A minute, and asked for again on the same beat.
     *
     * ⚠️ `staleTime` alone would have done nothing: the shell is a layout
     * route, so this mounts once for the whole session, and this project
     * turns `refetchOnWindowFocus` off. Stale with nothing to trigger a
     * fetch is simply old — the rail would have carried the number it was
     * born with until somebody reloaded, which is the opposite of a badge
     * that exists so a report arriving finds you on another screen.
     */
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  return data ?? 0;
};

export const useTicket = (id: string | undefined) =>
  useQuery({
    queryKey: ['ticket', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const data = await gqlClient.request<{ ticket: TicketDetail }>(TICKET, { id });
      return data.ticket;
    },
  });

/**
 * Everything that changes one report, sharing one cache update.
 *
 * Each mutation answers with the whole report, so the detail screen is
 * written from the server's answer rather than guessed at — and the list is
 * marked stale, because a status change moves a row between filters and a
 * reply changes the order.
 */
/**
 * What the three mutations are given. Two shapes, both of which name the
 * report — which is what `onError` needs and the only reason this is a union
 * rather than `object`.
 */
type TicketChangeInput =
  | { id: string; status: TicketStatus }
  | { input: { ticketId: string; body: string; status?: TicketStatus } };

const ticketIdOf = (input: TicketChangeInput): string =>
  'id' in input ? input.id : input.input.ticketId;

const useTicketChange = <TInput extends TicketChangeInput, TName extends string>(
  document: string,
  name: TName,
) => {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: async (input: TInput) => {
      const data = await gqlClient.request<Record<TName, TicketDetail>>(document, input);
      return data[name];
    },
    onSuccess: (ticket) => {
      cache.setQueryData(['ticket', ticket.id], ticket);
      // The list's order and filters both change under a status or a reply.
      void cache.invalidateQueries({ queryKey: ['tickets'] });
      /*
       * And the badge, which is the one somebody is looking at while they
       * work: a reply moves its report out of the count, and without this
       * the rail went on saying "3 waiting" for a minute after the third
       * was answered.
       */
      void cache.invalidateQueries({ queryKey: ['ticketsWaiting'] });
    },
    onError: (_error, input) => {
      /*
       * ⚠️ A failed mutation does not mean nothing happened. The API writes
       * a reply down before it sends the email, so a connection lost on the
       * way back leaves the answer stored and this side none the wiser —
       * and a person who pressed Send twice would send it twice. Reading
       * the report again is what settles it, and what the message on screen
       * promises has been done.
       */
      void cache.invalidateQueries({ queryKey: ['ticket', ticketIdOf(input)] });
    },
  });
};

export const useSetTicketStatus = () =>
  useTicketChange<{ id: string; status: TicketStatus }, 'setTicketStatus'>(
    SET_STATUS,
    'setTicketStatus',
  );

export const useReplyToTicket = () =>
  useTicketChange<
    { input: { ticketId: string; body: string; status?: TicketStatus } },
    'replyToTicket'
  >(REPLY, 'replyToTicket');

export const useAddTicketNote = () =>
  useTicketChange<{ input: { ticketId: string; body: string } }, 'addTicketNote'>(
    ADD_NOTE,
    'addTicketNote',
  );
